/**
 * @fileoverview Email Template Builder Page — WYSIWYG HTML editor for email templates.
 * @module email-templates
 *
 * Responsibilities: - Full-featured rich-text email editor backed by TipTap
 *                   - Data-placeholder insertion into editor content
 *                   - Three view modes: Edit (TipTap), Preview (rendered), Code (raw HTML)
 *                   - Create and update email template records via injected service callbacks
 * Dependencies:     - @tiptap/react, @tiptap/starter-kit, @tiptap/extension-underline, @tiptap/extension-text-align
 *                   - @firesquirrel/local-db (EmailTemplateLocal type)
 * Architecture:     - Stateless UI shell; all persistence delegated to prop callbacks (local-first service layer)
 *                   - No direct API calls; no fetch/axios/apiClient usage
 * Notes:            - Replaces the deprecated document.execCommand-based editor (G-EDITOR-FRAG)
 *                   - Shared between tenant-web (/email-templates/:id) and system-web (/document-templates)
 */

// ---------------------------------------------------------------------------
// Email Template Builder Page — Shared WYSIWYG HTML editor with data placeholders
// Used by both tenant-web and system-web
// ---------------------------------------------------------------------------

import { useState, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import {
  ArrowLeft,
  Save,
  Eye,
  Code,
  Type,
  List,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Variable,
  Undo,
  Redo,
  AlertCircle,
  CheckCircle,
  X,
} from 'lucide-react';
import type { EmailTemplateLocal } from '@firesquirrel/local-db';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * @contract EmailTemplateBuilderProps
 * Purpose: Dependency-injection contract for the email builder page.
 * Fields:  id (template id for edit mode), isNew, backPath, CRUD callbacks, nav/breadcrumb helpers
 * Rules:   All persistence is done via injected callbacks — no direct API calls
 * Sync:    local-first; caller provides service-backed callbacks
 */
export interface EmailTemplateBuilderProps {
  id?: string;
  isNew: boolean;
  backPath: string;
  getEmailTemplate: (id: string) => Promise<EmailTemplateLocal | null>;
  createEmailTemplate: (data: Partial<EmailTemplateLocal>) => Promise<EmailTemplateLocal>;
  updateEmailTemplate: (id: string, data: Partial<EmailTemplateLocal>) => Promise<EmailTemplateLocal | null>;
  onNavigate: (path: string, options?: { replace?: boolean }) => void;
  setBreadcrumbLabel?: (path: string, label: string) => void;
  currentUser?: string;
}

// ---------------------------------------------------------------------------
// Data Placeholders available for email templates
// ---------------------------------------------------------------------------

const DATA_PLACEHOLDERS = [
  { key: '{{recipientName}}', label: 'Recipient Name', category: 'Recipient' },
  { key: '{{recipientEmail}}', label: 'Recipient Email', category: 'Recipient' },
  { key: '{{entityName}}', label: 'Entity Name', category: 'Entity' },
  { key: '{{entityAddress}}', label: 'Entity Address', category: 'Entity' },
  { key: '{{entityCategory}}', label: 'Entity Category', category: 'Entity' },
  { key: '{{inspectionDate}}', label: 'Inspection Date', category: 'Inspection' },
  { key: '{{inspectorName}}', label: 'Inspector Name', category: 'Inspection' },
  { key: '{{caseNumber}}', label: 'Case Number', category: 'Case' },
  { key: '{{caseStatus}}', label: 'Case Status', category: 'Case' },
  { key: '{{violationCount}}', label: 'Violation Count', category: 'Violations' },
  { key: '{{dueDate}}', label: 'Due Date', category: 'Dates' },
  { key: '{{departmentName}}', label: 'Department Name', category: 'Department' },
  { key: '{{departmentPhone}}', label: 'Department Phone', category: 'Department' },
  { key: '{{departmentEmail}}', label: 'Department Email', category: 'Department' },
  { key: '{{reportName}}', label: 'Report Name', category: 'Report' },
  { key: '{{reportDate}}', label: 'Report Date', category: 'Report' },
  { key: '{{currentDate}}', label: 'Current Date', category: 'System' },
  { key: '{{currentYear}}', label: 'Current Year', category: 'System' },
];

const PLACEHOLDER_CATEGORIES = [...new Set(DATA_PLACEHOLDERS.map((p) => p.category))];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * @component EmailTemplateBuilderPage
 * Purpose:     Full-featured WYSIWYG email template editor with data placeholder insertion.
 * Props:       See EmailTemplateBuilderProps — all persistence via injected callbacks
 * Behavior:    TipTap-backed rich text edit, rendered preview, and raw HTML code view.
 *              Placeholder tokens (e.g. {{entityName}}) are inserted via TipTap insertContent command.
 * Data Source: local (Dexie via caller-provided service callbacks)
 * Constraints: No direct API calls; no document.execCommand
 * Performance: TipTap editor is initialized once; HTML synced via onUpdate callback
 */
export function EmailTemplateBuilderPage({
  id,
  isNew,
  backPath,
  getEmailTemplate,
  createEmailTemplate,
  updateEmailTemplate,
  onNavigate,
  setBreadcrumbLabel,
  currentUser = 'current-user',
}: EmailTemplateBuilderProps) {
  const [template, setTemplate] = useState<Partial<EmailTemplateLocal>>({
    name: '',
    description: '',
    status: 'draft',
    subjectTemplate: '',
    bodyHtml: getDefaultEmailHtml(),
    bodyPlainText: '',
    dataElements: [],
    version: 1,
  });

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState<'edit' | 'preview' | 'code'>('edit');
  const [showPlaceholders, setShowPlaceholders] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Track the last HTML value produced by the editor to prevent sync loops.
  // When template.bodyHtml changes from an external source (async load, code-view edit)
  // this ref allows the sync effect to distinguish those changes from editor-originated ones.
  const editorHtmlRef = useRef<string>(template.bodyHtml ?? '');

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: template.bodyHtml ?? getDefaultEmailHtml(),
    onUpdate: ({ editor: e }) => {
      const html = e.getHTML();
      editorHtmlRef.current = html;
      setTemplate((prev) => ({ ...prev, bodyHtml: html }));
    },
    editorProps: {
      attributes: {
        class: 'min-h-[380px] p-6 focus:outline-hidden',
        style: 'font-family: Arial, sans-serif; font-size: 14px; line-height: 1.6;',
      },
    },
  });

  // Sync editor content when template.bodyHtml changes from outside the editor
  // (async DB load, or code-view textarea edit). Skip if the editor itself produced the value.
  useEffect(() => {
    if (!editor) return;
    if (template.bodyHtml === editorHtmlRef.current) return;
    editor.commands.setContent(template.bodyHtml ?? '');
    editorHtmlRef.current = template.bodyHtml ?? '';
  }, [template.bodyHtml, editor]);

  useEffect(() => {
    if (!isNew && id) {
      loadTemplate(id);
    }
  }, [id, isNew]);

  const loadTemplate = async (templateId: string) => {
    try {
      const data = await getEmailTemplate(templateId);
      if (data) {
        setTemplate(data);
        if (setBreadcrumbLabel && data.name) {
          setBreadcrumbLabel(`/email-templates/${templateId}`, data.name);
        }
      }
    } catch (err) {
      console.error('[EmailTemplateBuilder] Failed to load template:', err);
      setErrorMessage('Failed to load template. Please go back and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!template.name?.trim()) {
      alert('Please enter a template name');
      return;
    }

    setSaving(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      const dataElements = extractDataElements(template.bodyHtml ?? '');

      if (isNew) {
        const created = await createEmailTemplate({
          name: template.name,
          description: template.description,
          status: 'draft',
          subjectTemplate: template.subjectTemplate ?? '',
          bodyHtml: template.bodyHtml ?? '',
          bodyPlainText: htmlToPlainText(template.bodyHtml ?? ''),
          dataElements,
          createdBy: currentUser,
          updatedBy: currentUser,
        });
        onNavigate(`/email-templates/${created._id}`, { replace: true });
      } else if (id) {
        await updateEmailTemplate(id, {
          name: template.name,
          description: template.description,
          subjectTemplate: template.subjectTemplate,
          bodyHtml: template.bodyHtml,
          bodyPlainText: htmlToPlainText(template.bodyHtml ?? ''),
          dataElements,
          updatedBy: currentUser,
        });
        setSuccessMessage('Template saved successfully.');
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err) {
      console.error('[EmailTemplateBuilder] Failed to save:', err);
      setErrorMessage('Failed to save template. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  /** Insert a data placeholder token at the current TipTap cursor position. */
  const handleInsertPlaceholder = (placeholder: string) => {
    editor?.chain().focus().insertContent(placeholder).run();
    setShowPlaceholders(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading template...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Error / Success Banners */}
      {errorMessage && (
        <div className="flex items-center gap-2 mx-6 mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span className="flex-1">{errorMessage}</span>
          <button onClick={() => setErrorMessage(null)} className="p-0.5 hover:bg-red-100 rounded-sm"><X className="h-4 w-4" /></button>
        </div>
      )}
      {successMessage && (
        <div className="flex items-center gap-2 mx-6 mt-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          <CheckCircle className="h-4 w-4 shrink-0" />
          <span className="flex-1">{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)} className="p-0.5 hover:bg-green-100 rounded-sm"><X className="h-4 w-4" /></button>
        </div>
      )}
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200">
        <div className="flex items-center gap-4">
          <button
            onClick={() => onNavigate(backPath)}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <input
              type="text"
              value={template.name}
              onChange={(e) => setTemplate((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Email Template Name"
              className="text-xl font-semibold text-gray-900 bg-transparent border-none focus:outline-hidden focus:ring-0 w-80"
            />
            <p className="text-sm text-gray-500">
              {isNew ? 'New email template' : `Last updated: ${template.updatedAt ? new Date(template.updatedAt).toLocaleDateString() : 'Never'}`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('edit')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'edit' ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Type className="h-4 w-4 inline mr-1" />
              Edit
            </button>
            <button
              onClick={() => setViewMode('preview')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'preview' ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Eye className="h-4 w-4 inline mr-1" />
              Preview
            </button>
            <button
              onClick={() => setViewMode('code')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'code' ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Code className="h-4 w-4 inline mr-1" />
              HTML
            </button>
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving…' : 'Save Template'}
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Editor Area */}
        <div className="flex flex-col flex-1 overflow-hidden">
          {/* Subject Line */}
          <div className="px-6 py-3 bg-white border-b border-gray-200">
            <label className="block text-xs font-medium text-gray-500 mb-1">Subject Line</label>
            <input
              type="text"
              value={template.subjectTemplate ?? ''}
              onChange={(e) => setTemplate((prev) => ({ ...prev, subjectTemplate: e.target.value }))}
              placeholder="e.g., Fire Inspection Report - {{entityName}}"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Toolbar (Edit mode only) */}
          {viewMode === 'edit' && (
            <div className="flex items-center gap-1 px-6 py-2 bg-gray-50 border-b border-gray-200">
              <ToolbarButton icon={Undo} onClick={() => editor?.chain().focus().undo().run()} title="Undo" />
              <ToolbarButton icon={Redo} onClick={() => editor?.chain().focus().redo().run()} title="Redo" />
              <div className="w-px h-6 bg-gray-300 mx-2" />
              <ToolbarButton
                icon={Bold}
                onClick={() => editor?.chain().focus().toggleBold().run()}
                title="Bold"
                active={editor?.isActive('bold')}
              />
              <ToolbarButton
                icon={Italic}
                onClick={() => editor?.chain().focus().toggleItalic().run()}
                title="Italic"
                active={editor?.isActive('italic')}
              />
              <ToolbarButton
                icon={UnderlineIcon}
                onClick={() => editor?.chain().focus().toggleUnderline().run()}
                title="Underline"
                active={editor?.isActive('underline')}
              />
              <div className="w-px h-6 bg-gray-300 mx-2" />
              <ToolbarButton
                icon={AlignLeft}
                onClick={() => editor?.chain().focus().setTextAlign('left').run()}
                title="Align Left"
                active={editor?.isActive({ textAlign: 'left' })}
              />
              <ToolbarButton
                icon={AlignCenter}
                onClick={() => editor?.chain().focus().setTextAlign('center').run()}
                title="Align Center"
                active={editor?.isActive({ textAlign: 'center' })}
              />
              <ToolbarButton
                icon={AlignRight}
                onClick={() => editor?.chain().focus().setTextAlign('right').run()}
                title="Align Right"
                active={editor?.isActive({ textAlign: 'right' })}
              />
              <div className="w-px h-6 bg-gray-300 mx-2" />
              <ToolbarButton
                icon={List}
                onClick={() => editor?.chain().focus().toggleBulletList().run()}
                title="Bullet List"
                active={editor?.isActive('bulletList')}
              />
              <div className="w-px h-6 bg-gray-300 mx-2" />
              <div className="relative">
                <button
                  onClick={() => setShowPlaceholders(!showPlaceholders)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
                >
                  <Variable className="h-4 w-4" />
                  Insert Data
                </button>
                {showPlaceholders && (
                  <PlaceholderDropdown
                    onSelect={handleInsertPlaceholder}
                    onClose={() => setShowPlaceholders(false)}
                  />
                )}
              </div>
            </div>
          )}

          {/* Editor / Preview / Code */}
          <div className="flex-1 overflow-auto p-6">
            {viewMode === 'edit' && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-xs overflow-auto focus-within:ring-2 focus-within:ring-blue-500">
                <EditorContent editor={editor} />
              </div>
            )}

            {viewMode === 'preview' && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-xs overflow-hidden">
                <div className="px-4 py-2 bg-gray-100 border-b border-gray-200 text-sm text-gray-600">
                  <strong>Subject:</strong> {renderPreview(template.subjectTemplate ?? '')}
                </div>
                <div
                  className="p-6"
                  style={{ fontFamily: 'Arial, sans-serif', fontSize: '14px', lineHeight: '1.6' }}
                  dangerouslySetInnerHTML={{ __html: renderPreview(template.bodyHtml ?? '') }}
                />
              </div>
            )}

            {viewMode === 'code' && (
              <textarea
                value={template.bodyHtml}
                onChange={(e) => setTemplate((prev) => ({ ...prev, bodyHtml: e.target.value }))}
                className="w-full h-full min-h-[400px] p-4 font-mono text-sm bg-gray-900 text-green-400 rounded-lg border-none focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                spellCheck={false}
              />
            )}
          </div>
        </div>

        {/* Right Sidebar - Template Info */}
        <aside className="w-80 bg-white border-l border-gray-200 overflow-y-auto">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-medium text-gray-900 mb-3">Template Details</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
                <textarea
                  value={template.description ?? ''}
                  onChange={(e) => setTemplate((prev) => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describe this email template..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                <select
                  value={template.status}
                  onChange={(e) => setTemplate((prev) => ({ ...prev, status: e.target.value as any }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>
          </div>

          <div className="p-4">
            <h3 className="font-medium text-gray-900 mb-3">Data Placeholders</h3>
            <p className="text-xs text-gray-500 mb-3">
              Click to copy, then paste into your email content.
            </p>
            <div className="space-y-3">
              {PLACEHOLDER_CATEGORIES.map((category) => (
                <div key={category}>
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">{category}</p>
                  <div className="flex flex-wrap gap-1">
                    {DATA_PLACEHOLDERS.filter((p) => p.category === category).map((p) => (
                      <button
                        key={p.key}
                        onClick={() => navigator.clipboard.writeText(p.key)}
                        className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-sm hover:bg-blue-100 hover:text-blue-700 transition-colors"
                        title={`Click to copy: ${p.key}`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toolbar Button
// ---------------------------------------------------------------------------

/**
 * @reusable ToolbarButton
 * Usage:  Rich-text toolbar action button with optional active state indicator.
 * Rules:  Uses onMouseDown + preventDefault to prevent editor focus loss on click.
 */
function ToolbarButton({
  icon: Icon,
  onClick,
  title,
  active,
}: {
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  title: string;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={title}
      className={`p-2 rounded transition-colors ${
        active
          ? 'text-amber-700 bg-amber-100'
          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
      }`}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Placeholder Dropdown
// ---------------------------------------------------------------------------

/**
 * @reusable PlaceholderDropdown
 * Usage:  Floating menu that lists all DATA_PLACEHOLDERS grouped by category.
 *         Selecting a placeholder calls onSelect(key), which inserts it at the TipTap cursor.
 * Rules:  Requires a backdrop overlay (fixed inset-0) for click-outside dismissal.
 */
function PlaceholderDropdown({
  onSelect,
  onClose,
}: {
  onSelect: (placeholder: string) => void;
  onClose: () => void;
}) {
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
        {PLACEHOLDER_CATEGORIES.map((category) => (
          <div key={category} className="p-2">
            <p className="text-xs font-medium text-gray-500 uppercase px-2 mb-1">{category}</p>
            {DATA_PLACEHOLDERS.filter((p) => p.category === category).map((p) => (
              <button
                key={p.key}
                onClick={() => onSelect(p.key)}
                className="w-full text-left px-2 py-1.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 rounded-sm"
              >
                {p.label}
                <span className="text-xs text-gray-400 ml-2">{p.key}</span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getDefaultEmailHtml(): string {
  return `
<div style="max-width: 600px; margin: 0 auto; font-family: Arial, sans-serif;">
  <div style="background-color: #1e40af; color: white; padding: 20px; text-align: center;">
    <h1 style="margin: 0; font-size: 24px;">{{departmentName}}</h1>
  </div>
  
  <div style="padding: 30px; background-color: #ffffff;">
    <p>Dear {{recipientName}},</p>
    
    <p>Please find attached your requested report.</p>
    
    <div style="background-color: #f3f4f6; padding: 15px; border-radius: 8px; margin: 20px 0;">
      <p style="margin: 0;"><strong>Report:</strong> {{reportName}}</p>
      <p style="margin: 5px 0 0;"><strong>Date:</strong> {{reportDate}}</p>
    </div>
    
    <p>If you have any questions, please contact us at {{departmentEmail}} or {{departmentPhone}}.</p>
    
    <p>Best regards,<br>{{departmentName}}</p>
  </div>
  
  <div style="background-color: #f3f4f6; padding: 15px; text-align: center; font-size: 12px; color: #6b7280;">
    <p style="margin: 0;">© {{currentYear}} {{departmentName}}. All rights reserved.</p>
  </div>
</div>
  `.trim();
}

function extractDataElements(html: string): string[] {
  const matches = html.match(/\{\{(\w+)\}\}/g) ?? [];
  return [...new Set(matches.map((m) => m.replace(/[{}]/g, '')))];
}

function htmlToPlainText(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent ?? '';
}

function renderPreview(content: string): string {
  const sampleData: Record<string, string> = {
    recipientName: 'John Smith',
    recipientEmail: 'john.smith@example.com',
    entityName: 'ABC Restaurant',
    entityAddress: '123 Main St, Springfield, IL 62701',
    entityCategory: 'Restaurant',
    inspectionDate: new Date().toLocaleDateString(),
    inspectorName: 'Jane Doe',
    caseNumber: 'INS-2024-001234',
    caseStatus: 'Completed',
    violationCount: '3',
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString(),
    departmentName: 'Springfield Fire Department',
    departmentPhone: '(555) 123-4567',
    departmentEmail: 'fire@springfield.gov',
    reportName: 'Monthly Inspection Summary',
    reportDate: new Date().toLocaleDateString(),
    currentDate: new Date().toLocaleDateString(),
    currentYear: new Date().getFullYear().toString(),
  };

  return content.replace(/\{\{(\w+)\}\}/g, (_, key) => sampleData[key] ?? `{{${key}}}`);
}

export default EmailTemplateBuilderPage;
